import React, {Component, Children} from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import t from 'tcomb';
import {validate} from 'tcomb-validation';

import * as ObjectHelper from 'js/common/object_helper';
import * as CountryRegionHelper from 'js/country_region_helper/index';

import {ChildrenComponents} from 'js/components/prop_types';
import {FullString} from 'js/tcomb_types';
import GoogleMapsApi, {transformPlaceDetailsToUserProfileDetails} from 'js/google/maps/index';

import CountryInput from 'js/components/country_input';
import AddressInput from './components/address_input';
import AddressLine2Input from './components/address_line_2_input';
import PostalCodeInput from './components/postal_code_input';
import CityInput from './components/city_input';
import StateInput from './components/state_input';

import './index.scss';

class AddressInfoForm extends Component {
    static propTypes = {
        address: PropTypes.string,
        address_line_2: PropTypes.string,
        country: PropTypes.string,
        state: PropTypes.string,
        zip_code: PropTypes.string,
        GoogleMapsApi: PropTypes.object,
        children: ChildrenComponents,
        onChange: PropTypes.func,
        layout: PropTypes.oneOf(['full', 'compact'])
    };

    static defaultProps = {
        onChange: () => {},
        address: '',
        address_line_2: '',
        country: '',
        state: '',
        city: '',
        zip_code: '',
        GoogleMapsApi,
        layout: 'full'
    };

    /** Use these to control where children are rendered. */
    static slots = {
        before_form: 'before_form',
        after_form: 'after_form'
    };

    constructor(props) {
        super(props);

        this.state = {
            address: props.address,
            address_line_2: props.address_line_2,
            country: props.country,
            state: props.state,
            city: props.city,
            zip_code: props.zip_code
        };
    }

    /**
     * @param {Object} props
     * @memberof AddressInfoForm
     */
    getType = props => ({
        address: t.maybe(FullString, 'AddressType'),
        address_line_2: t.maybe(FullString, 'AddressLine2Type'),
        country: t.maybe(t.enums(CountryRegionHelper.CountryMap, 'CountryMapEnum'), 'CountryType'),
        state: props.country ? t.enums(
            CountryRegionHelper.RegionsByCountryShortCode[props.country],
            'CountryStateEnum'
        ) : null,
        city: t.maybe(FullString, 'CityType'),
        zip_code: t.maybe(FullString, 'ZipCodeType')
    });

    /**
     * @param {string} fieldName
     * @param {*} value
     * @memberof AddressInfoForm
     */
    setFormValue = R.curry((fieldName, value) => {
        const state = R.cond([
            [R.equals('country'), R.always('')],
            [R.equals('state'), R.always(value)],
            [R.T, R.always(this.state.state)]
        ])(fieldName);

        this.setState(
            {
                [fieldName]: value,
                state
            },
            () => {
                R.pipe(
                    ObjectHelper.mapObj(R.ifElse(R.equals(''), R.always(null), R.identity)),
                    this.props.onChange
                )(this.state);
            }
        );
    });

    handlePlaceSelected = place => {
        this.setState(transformPlaceDetailsToUserProfileDetails(place), () => {
            this.props.onChange(this.state);
        });
    };

    /**
     * Useful when mounting this component as a ref
     * if you would like to know if its state is valid according
     * to its own logic
     */
    isValid = () =>
        R.pipe(
            R.keys,
            R.all(key => this.validate(key) === 'success')
        )(this.state);

    /**
     * @param {string} fieldName
     * @memberof AddressInfoForm
     */
    validate = fieldName => {
        const field = this.state[fieldName];
        const type = this.getType(this.state)[fieldName];

        if (R.isEmpty(field) || R.isNil(type)) {
            return null;
        }

        return validate(field, type).isValid() ? 'success' : 'error';
    };

    /**
     * Partition children by slot. Children that do not provide a slot will not be rendered.
     * @todo Might want to helperize this
     */
    renderFillSlotsWithChildren = () => {
        const childrenAsArray = Children.toArray(this.props.children);

        if (R.isEmpty(childrenAsArray)) {
            return {childrenBeforeForm: null, childrenAfterForm: null};
        }

        return {
            childrenBeforeForm: R.filter(
                R.pipe(R.path(['props', 'slot']), R.equals(AddressInfoForm.slots.before_form))
            )(childrenAsArray),
            childrenAfterForm: R.filter(
                R.pipe(R.path(['props', 'slot']), R.equals(AddressInfoForm.slots.after_form))
            )(childrenAsArray)
        };
    };

    renderAddress = () => (
        <AddressInput
            value={this.state.address}
            onChange={this.setFormValue('address')}
            onPlaceSelected={this.handlePlaceSelected}
            validationState={this.validate('address')}
            GoogleMapsApi={this.props.GoogleMapsApi}
        />
    );

    renderAddressLine2 = () => (
        <AddressLine2Input
            value={this.state.address_line_2}
            onChange={this.setFormValue('address_line_2')}
            validationState={this.validate('address_line_2')}
        />
    );

    renderCity = () => (
        <CityInput
            value={this.state.city}
            onChange={this.setFormValue('city')}
            validationState={this.validate('city')}
        />
    );

    renderPostalCode = () => (
        <PostalCodeInput
            value={this.state.zip_code}
            onChange={this.setFormValue('zip_code')}
            validationState={this.validate('zip_code')}
        />
    );

    renderCountry = () => (
        <CountryInput
            value={this.state.country}
            onChange={this.setFormValue('country')}
            validationState={this.validate('country')}
        />
    );

    renderState = () => (
        <StateInput
            value={this.state.state}
            onChange={this.setFormValue('state')}
            validationState={this.validate('state')}
            country={this.state.country}
        />
    );

    /**
     * Since these are controlled inputs
     * we use individual components so that we can control
     * shouldComponentUpdate on them
     * to avoid extra re-renders when the state concerning a given input
     * has not changed.
     */
    render() {
        const {layout} = this.props;
        const {childrenBeforeForm, childrenAfterForm} = this.renderFillSlotsWithChildren();

        if (layout === 'full') {
            return (
                <div className="addressinfo-container">
                    {childrenBeforeForm}
                    {this.renderAddress()}
                    {this.renderAddressLine2()}
                    {this.renderCity()}
                    {this.renderCountry()}
                    {this.renderState()}
                    {this.renderPostalCode()}
                    {childrenAfterForm}
                </div>
            );
        }

        if (layout === 'compact') {
            return (
                <div className="addressinfo-container">
                    <div className="row">{childrenBeforeForm}</div>

                    {this.renderAddress()}
                    {this.renderAddressLine2()}

                    <div className="row">
                        <div className="col-xs-6">{this.renderCountry()}</div>
                        <div className="col-xs-6">{this.renderState()}</div>
                    </div>

                    <div className="row">
                        <div className="col-xs-8">{this.renderCity()}</div>

                        <div className="col-xs-4">{this.renderPostalCode()}</div>
                    </div>

                    <div className="row">{childrenAfterForm}</div>
                </div>
            );
        }
    }
}

export const addressInfoKeys = [
    'address',
    'address_line_2',
    'country',
    'state',
    'city',
    'zip_code'
];

export default AddressInfoForm;



// WEBPACK FOOTER //
// ./creator/components/address_info_form/index.js